import React, { useEffect, useRef, useState } from 'react'
import * as echarts from 'echarts'
import {
    WalletIcon,
    LockClosedIcon,
    UserIcon,
    TicketIcon,
    XCircleIcon,
    BanknotesIcon,
    PlusCircleIcon,
    UsersIcon,
} from '@heroicons/react/24/solid'
import { Select } from 'components/select'
import stSeat from '../../assets/images/icons/st_seat.svg'
import vipSeat from '../../assets/images/icons/vip_seat.svg'
import PaymentMethodsChart from './components/PaymentMethodsChart'
import { useLazyQuery } from '@apollo/client'
import { GET_REVENUE_DASHBOARD } from 'graphql/dashboard/queries'
import { DATE_RANGES, DateFilter } from './components/DateFilter'
import { DashboardSkeleton } from './components/DashboardSkeleton'
import i18next from 'i18next'
import { GraphQlResponseNoData } from 'components/custom/GraphQlResponseDisplay'
import { useTranslation } from 'react-i18next'
import { BoatIcon } from 'components/icons'
import html2pdf from 'html2pdf.js'
import { PrinterIcon } from '@heroicons/react/24/outline'

const seriesLabel = {
    show: true,
}

const StatCard = ({ title, value, type }) => {
    const getIconConfig = () => {
        switch (type) {
            case 'gross_revenue':
                return {
                    icon: <BanknotesIcon className="w-5 h-5 text-blue/90" />,
                    iconBg: 'bg-blue/10',
                }
            case 'net_revenue':
                return {
                    icon: <BanknotesIcon className="w-5 h-5 text-[#caa72b]" />,
                    iconBg: 'bg-[#f6e7c1]',
                }
            case 'std_net_revenue':
            case 'day_pass_net_revenue':
                return {
                    icon: <BanknotesIcon className="w-5 h-5 text-[#caa72b]" />,
                    iconBg: 'bg-[#f6e7c1]',
                }
            case 'wallet_refunds':
                return {
                    icon: <WalletIcon className="w-5 h-5 text-green/90" />,
                    iconBg: 'bg-green/10',
                }
            case 'wallet_spend':
                return {
                    icon: <WalletIcon className="w-5 h-5 text-orange-700" />,
                    iconBg: 'bg-orange-100',
                }
            case 'dpRevenue':
                return {
                    icon: <WalletIcon className="w-5 h-5 text-primary" />,
                    iconBg: 'bg-[#e0e7ef]',
                }
            case 'stdRevenue':
                return {
                    icon: <BanknotesIcon className="w-5 h-5 text-lime-700" />,
                    iconBg: 'bg-lime-200',
                }
            case 'vipSeatRevenue':
                return {
                    icon: <img src={vipSeat} className="w-[20px]" alt="VIP" />,
                    iconBg: 'bg-[#d4b242]/30',
                }
            case 'stdSeatRevenue':
                return {
                    icon: <img src={stSeat} className="w-[20px] " alt="Standard" />,
                    iconBg: 'bg-[#6fa0ad]/20',
                }
            case 'std_bookings':
                return {
                    icon: <BoatIcon className="w-5 h-5 fill-primary/90" />,
                    iconBg: 'bg-primary/10',
                }
            case 'day_pass_bookings':
                return {
                    icon: <TicketIcon className="w-5 h-5 text-primary/90" />,
                    iconBg: 'bg-primary/10',
                }
            case 'bookings':
                return {
                    icon: <PlusCircleIcon className="w-5 h-5 text-blue/90" />,
                    iconBg: 'bg-blue/10',
                }
            case 'passengers':
                return {
                    icon: <UsersIcon className="w-5 h-5 text-primary/90" />,
                    iconBg: 'bg-primary/10',
                }
            default:
                return {
                    icon: <BanknotesIcon className="w-5 h-5 text-primary" />,
                    iconBg: 'bg-primary/90',
                }
        }
    }

    const { icon, iconBg } = getIconConfig()

    return (
        <div className="bgwhite p-4 rounded-lg shadow-lg border border-gray-100 flex items-center justify-between">
            <div>
                <h3 className="text-gray-900 text-sm font-semibold">{title}</h3>
                <p className="text-md font-bold text-primary">{value}</p>
            </div>
            <div className={`${iconBg} p-3 rounded-xl py-2 px-2 flex items-center justify-center`}>{icon}</div>
        </div>
    )
}

const ProgressBar = ({ label, percentage, ageRange }) => (
    <div className="mb-4">
        <div className="flex justify-between mb-1">
            <span className="text-gray-800 text-sm font-semibold">{label}</span>
            <span className="text-gray-600 text-md font-bold">{percentage}%</span>
        </div>
        <div className="w-full h-2 bg-gray-200 rounded">
            <div
                className="h-full rounded"
                style={{
                    width: `${percentage}%`,
                    background: 'linear-gradient(to right, #1f515d, #83b6c3)',
                }}
            ></div>
        </div>
        <span className="text-xs text-gray-500">{ageRange}</span>
    </div>
)

const StatProgressBar = ({ label, percentage, range }) => (
    <div className="flex items-center gap-4 mb-4">
        {label === 'Children' ? (
            <div className="bg-primary/40 p-2 rounded-lg">
                <UserIcon className="w-5 h-5 text-primary" />
            </div>
        ) : label === 'Young-Adults' ? (
            <div className="bg-[#d4b242]/30 p-2 rounded-lg">
                <UserIcon className="w-5 h-5 text-[#d4b242]" />
            </div>
        ) : label === 'Adults' ? (
            <div className="bg-primary/20 p-2 rounded-lg">
                <UserIcon className="w-5 h-5 text-primary" />
            </div>
        ) : label === 'Seniors' ? (
            <div className="bg-primary/5 p-2 rounded-lg">
                <UserIcon className="w-5 h-5 text-primary/40" />
            </div>
        ) : null}
        <div className="flex-1">
            <div className="flex justify-between mb-1">
                <div>
                    <span className="text-gray-800 font-medium">{label}</span>
                    <span className="text-gray-400 text-sm ml-2">{range}</span>
                </div>
                <span className="text-gray-600 text-md font-bold">{percentage}%</span>
            </div>
            <div className="w-full h-[12px] bg-gray-100 rounded-full">
                <div
                    className="h-full rounded-full"
                    style={{
                        width: `${percentage}%`,
                        background:
                            label === 'Young-Adults' ? '#caa72b' : 'linear-gradient(to right, #5d8e9a, #83b6c3)',
                    }}
                ></div>
            </div>
        </div>
    </div>
)

const RouteProgress = ({ route, value, maxValue }) => (
    <div className="mb-4">
        <div className="flex justify-between items-center mb-1">
            <span className="text-primary text-sm font-medium">{route}</span>
            <span
                className={`!text-[16px] whitespace-nowrap font-bold ${
                    parseFloat(value) < 0 ? 'text-red/80' : 'text-gray-600'
                }`}
            >
                BD {value}
            </span>
        </div>
        <div className="w-full h-[3px] bg-gray-100 rounded-full overflow-hidden">
            <div
                className={`h-full rounded-full ${
                    parseFloat(value) < 0
                        ? 'bg-gradient-to-r from-red/90 to-red/15'
                        : 'bg-gradient-to-r from-[#5d8e9a] to-[#83b6c3]'
                }`}
                style={{ width: `${Math.abs((value / maxValue) * 100)}%` }}
            ></div>
        </div>
    </div>
)

const ROUTE_TYPES = {
    ALL: null,
    DOMESTIC: 'DMC',
    INTERNATIONAL: 'INL',
}

const RevenueDashboard = () => {
    const { t } = useTranslation()
    const [dashboardData, setDashboardData] = useState(null)
    const [selectedRouteType, setSelectedRouteType] = useState(ROUTE_TYPES.ALL)
    const chartRef = useRef(null)

    const [getRevenueDashboard, { loading }] = useLazyQuery(GET_REVENUE_DASHBOARD, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.getRevenueDashboard?.success) {
                setDashboardData(data.getRevenueDashboard.revenue_dashboard)
                updateCharts(data.getRevenueDashboard.revenue_dashboard)
            }
        },
    })

    const [currentDateFilter, setCurrentDateFilter] = useState({ type: DATE_RANGES.TODAY, from: null, to: null })

    const handleDateChange = ({ from, to, type }) => {
        setCurrentDateFilter({ from, to, type })
        if (type === 'CUSTOM_RANGE') {
            getRevenueDashboard({
                variables: {
                    dateRange: {
                        start_date: from,
                        end_date: to,
                    },
                    routeType: selectedRouteType,
                },
            })
        } else {
            getRevenueDashboard({
                variables: {
                    period: type,
                    routeType: selectedRouteType,
                },
            })
        }
    }

    const handleRouteTypeChange = (routeType) => {
        setSelectedRouteType(routeType)
        const { from, to, type } = currentDateFilter
        if (type === 'CUSTOM_RANGE') {
            getRevenueDashboard({
                variables: {
                    dateRange: {
                        start_date: from,
                        end_date: to,
                    },
                    routeType: routeType,
                },
            })
        } else {
            getRevenueDashboard({
                variables: {
                    period: type,
                    routeType: routeType,
                },
            })
        }
    }

    const updateCharts = data => {
        if (!data?.station_revenues?.length) return

        // Add a small delay and check DOM readiness
        const initChart = () => {
            if (!chartRef.current) {
                setTimeout(initChart, 100)
                return
            }

            echarts.dispose(chartRef.current)

            const chart = echarts.init(chartRef.current)
            const stationNames = data.station_revenues.map(
                station => station?.station_name?.[i18next?.language || 'en'] || 'N/A'
            )
            const startNetRevenue = data.station_revenues.map(station => station?.start_station_net_revenue ?? 0)
            const endNetRevenue = data.station_revenues.map(station => station?.end_station_net_revenue ?? 0)

            const option = {
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'shadow',
                    },
                    formatter: function (params) {
                        let content = `<div class="font-medium">
                            <div class="text-sm mb-1 text-primary font-semibold">${params[0].axisValue}</div>
                            <div class="flex flex-col gap-1 text-xs">`
                        params.forEach(p => {
                            content += `<div>
                                <span style="display:inline-block;width:10px;height:10px;background:${
                                    p.color
                                };border-radius:2px;margin-right:4px"></span>
                                ${p.seriesName}: <strong class="text-gray-700">BD ${Number(p.data).toFixed(2)}</strong>
                            </div>`
                        })
                        content += `</div></div>`
                        return content
                    },
                },
                grid: {
                    left: '0%',
                    right: '0%',
                    bottom: '15%',
                    top: '3%',
                    containLabel: true,
                },
                xAxis: {
                    type: 'category',
                    data: stationNames.map(name => name.split(' ').join('\n')),
                    axisLabel: {
                        color: '#666',
                        interval: 0,
                        formatter: function (value) {
                            return value.split('\n').join('\n')
                        },
                        lineHeight: 15,
                        align: 'center',
                    },
                    axisLine: {
                        show: true,
                        lineStyle: {
                            color: '#edf2f7',
                        },
                    },
                },
                yAxis: {
                    type: 'value',
                    axisLabel: {
                        show: true,
                        color: '#374151',
                    },
                    splitLine: {
                        show: true,
                        lineStyle: {
                            color: '#edf2f7',
                        },
                    },
                    axisLine: {
                        show: true,
                        lineStyle: {
                            color: '#edf2f7',
                        },
                    },
                },
                series: [
                    {
                        name: 'Start Station Net Revenue',
                        type: 'bar',
                        data: startNetRevenue,
                        color: '#0c505b',
                        barWidth: '35%',
                        itemStyle: {
                            borderRadius: [3, 3, 0, 0],
                            shadowColor: 'rgba(0, 0, 0, 0.1)',
                            shadowBlur: 5,
                        },
                        label: {
                            show: true,
                            position: 'insideBottom',
                            color: '#ffffff',
                            fontSize: 12,
                            rotate: 90,
                            distance: 20,
                            formatter: function (params) {
                                return `${Number(params.data).toFixed(2)}`
                            },
                            verticalAlign: 'middle',
                            textBorderColor: '#0c505b',
                            textBorderWidth: 2,
                            textShadowBlur: 2,
                        },
                        emphasis: {
                            itemStyle: {
                                color: '#6fa0ad',
                            },
                        },
                    },
                    {
                        name: 'End Station Net Revenue',
                        type: 'bar',
                        data: endNetRevenue,
                        color: '#caa72b',
                        barWidth: '35%',
                        itemStyle: {
                            borderRadius: [3, 3, 0, 0],
                            shadowColor: 'rgba(0, 0, 0, 0.1)',
                            shadowBlur: 5,
                        },
                        label: {
                            show: true,
                            position: 'insideBottom',
                            color: '#ffffff',
                            fontSize: 12,
                            rotate: 90,
                            distance: 20,
                            formatter: function (params) {
                                return `${Number(params.data).toFixed(2)}`
                            },
                            verticalAlign: 'middle',
                            textBorderColor: '#caa72b',
                            textBorderWidth: 2,
                            textShadowBlur: 2,
                        },
                        emphasis: {
                            itemStyle: {
                                color: '#d4b242',
                            },
                        },
                    },
                ],
            }

            chart.setOption(option)

            const handleResize = () => {
                chart && chart.resize()
            }
            window.addEventListener('resize', handleResize)

            return () => {
                window.removeEventListener('resize', handleResize)
                chart && chart.dispose()
            }
        }

        initChart()
    }

    useEffect(() => {
        getRevenueDashboard({
            variables: {
                period: DATE_RANGES.TODAY,
                    routeType: null
            },
        })
    }, [])

    useEffect(() => {
        if (dashboardData && chartRef.current) {
            updateCharts(dashboardData)
        }
    }, [dashboardData])

    useEffect(() => {
        if (dashboardData && chartRef.current) {
            updateCharts(dashboardData)
        }
    }, [i18next.language])

    const handleDownloadPDF = () => {
        const element = document.getElementById('dashboard-pdf-content')
        if (!element) return
        // Add force-print class to enable print styles
        element.classList.add('force-print')
        html2pdf()
            .set({
                margin: 0.5,
                filename: 'revenue_dashboard.pdf',
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true },
                jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
            })
            .from(element)
            .save()
            .then(() => {
                // Remove force-print class after PDF is generated
                element.classList.remove('force-print')
            })
            .catch(() => {
                element.classList.remove('force-print')
            })
    }

    return (
        <div className="w-full print:w-full print:bg-white print:p-2">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6 print:flex-col print:items-stretch print:gap-2 print:mb-2">
                <h1 className="text-2xl font-bold text-gray-800 w-full md:w-auto text-center md:text-left print:text-lg print:font-bold">
                    {t('Revenue Overview')}
                </h1>
                <div className="flex gap-2 w-full md:w-auto print:w-full print:gap-1">
                  
                    <div className="w-[150px] print:hidden">
                        <Select
                            value={selectedRouteType || ''}
                            onChange={e => handleRouteTypeChange(e.target.value || null)}
                            className="w-full"
                        >
                            <option value="">{t('All Routes')}</option>
                            <option value={ROUTE_TYPES.DOMESTIC}>{t('Domestic')}</option>
                            <option value={ROUTE_TYPES.INTERNATIONAL}>{t('International')}</option>
                        </Select>
                    </div>
                    <DateFilter onDateChange={handleDateChange} className="w-full md:w-auto print:hidden" />
                      <button
                        onClick={handleDownloadPDF}
                        className="bg-primary text-white px-3 py-1 rounded-lg text-sm shadow hover:bg-primary/80 transition flex items-center gap-2 print:hidden"
                    >
                        <PrinterIcon className="w-4 h-4" />
                        {t('Download PDF')}
                    </button>
                </div>
            </div>
            <div id="dashboard-pdf-content" className="print:bg-white print:p-2">
                {loading ? (
                    <DashboardSkeleton />
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6 print:grid-cols-3 print:gap-2 print:mb-2">
                            <StatCard
                                title={t('Net Revenue')}
                                value={`BHD ${dashboardData?.net_revenue || 0}`}
                                type="net_revenue"
                            />
                            <StatCard
                                title={t('Wallet Spend')}
                                value={`BHD ${dashboardData?.wallet_spend || 0}`}
                                type="wallet_spend"
                            />
                            <StatCard
                                title={t('Wallet Refund')}
                                value={`BHD ${dashboardData?.wallet_refunds || 0}`}
                                type="wallet_refunds"
                            />
                            <StatCard
                                title={t('Gross Revenue')}
                                value={`BHD ${dashboardData?.gross_revenue || 0}`}
                                type="gross_revenue"
                            />
                            <StatCard
                                title={t('Standard Net Revenue')}
                                value={`BHD ${dashboardData?.std_net_revenue || 0}`}
                                type="std_net_revenue"
                            />
                            <StatCard
                                title={t('Day-Pass Net Revenue')}
                                value={`BHD ${dashboardData?.day_pass_net_revenue || 0}`}
                                type="day_pass_net_revenue"
                            />
                            <StatCard
                                title={t('Charter Boat Net Revenue')}
                                value={`BHD ${dashboardData?.charter_boat_net_revenue || 0}`}
                                type="day_pass_net_revenue"
                            />
                            <StatCard
                                title={t('Standard Seat Revenue')}
                                value={`BHD ${dashboardData?.gross_std_seat_revenue || 0}`}
                                type="stdSeatRevenue"
                            />
                            <StatCard
                                title={t('VIP Seat Revenue')}
                                value={`BHD ${dashboardData?.gross_vip_seat_revenue || 0}`}
                                type="vipSeatRevenue"
                            />

                            <StatCard
                                title={t('Standard Bookings')}
                                value={dashboardData?.std_bookings || 0}
                                type="std_bookings"
                            />
                            <StatCard
                                title={t('Day-Pass Bookings')}
                                value={dashboardData?.day_pass_bookings || 0}
                                type="day_pass_bookings"
                            />
                            <StatCard
                                title={t('Charter Boat Booking')}
                                value={dashboardData?.charter_boat_bookings || 0}
                                type="day_pass_bookings"
                            />
                            <StatCard
                                title={t('Standard Bookings(day-pass)')}
                                value={dashboardData?.daypass_std_bookings || 0}
                                type="bookings"
                            />
                            <StatCard
                                title={t('Passengers Transported')}
                                value={dashboardData?.passenger_transported || 0}
                                type="passengers"
                            />
                        </div>

                        <div className="flex flex-col lg:flex-row gap-4 print:flex-col print:gap-2">
                            {/* Revenue by Station Chart */}
                            <div className="bgwhite p-4 rounded-xl shadow-lg border border-gray-100 w-full lg:w-[60%] pb-0 print:w-full">
                                <h2 className="text-lg font-semibold mb-4">{t('Revenue by Station')}</h2>
                                {!dashboardData?.station_revenues?.length ? (
                                    <GraphQlResponseNoData className="!py-10 !mt-0" />
                                ) : (
                                    <div className="overflow-x-auto print:overflow-visible print:min-w-0 print:w-full">
                                        <div className="min-w-[800px] lg:min-w-full print:min-w-0 print:w-full">
                                            <div ref={chartRef} style={{ height: '250px' }} className="print:w-full" />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Payment Methods */}
                            <div className="bgwhite p-4 rounded-xl shadow-lg border border-gray-100 w-full lg:w-[40%] print:w-full">
                                <h2 className="text-lg font-semibold mb-4">{t('Payment Methods')}</h2>
                                {!dashboardData?.payment_method_revenues?.length ? (
                                    <GraphQlResponseNoData className="!py-10 !mt-0" />
                                ) : (
                                    <PaymentMethodsChart paymentData={dashboardData?.payment_method_revenues} />
                                )}
                            </div>
                        </div>

                        <div className="flex flex-col lg:flex-row gap-4 mt-6 print:flex-col print:gap-2 print:mt-2">
                            {/* Revenue by Routes */}
                            <div className="bgwhite p-4 rounded-xl shadow-lg border border-gray-100 w-full lg:w-[55%] print:w-full">
                                <h2 className="text-lg font-semibold mb-4">{t('Revenue by Route')}</h2>
                                {!dashboardData?.route_revenues?.length ? (
                                    <GraphQlResponseNoData className="!py-10 !h-[90%] !mt-0" />
                                ) : (
                                    <div className="space-y-4">
                                        {dashboardData?.route_revenues?.map((route, index) => {
                                            const maxRevenue = Math.max(
                                                ...dashboardData.route_revenues.map(r => r.net_revenue || 0)
                                            )
                                            return (
                                                <RouteProgress
                                                    key={index}
                                                    route={route.route_name[i18next?.language || 'en']}
                                                    value={route.net_revenue?.toFixed(2) || 0}
                                                    maxValue={maxRevenue}
                                                />
                                            )
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Revenue Overview */}
                            <div className="bgwhite p-4 rounded-xl shadow-lg border border-gray-100 w-full lg:w-[45%] print:w-full">
                                <div className="mb-4">
                                    <h2 className="text-lg font-semibold mb-4">{t('Top Performing Captains')}</h2>
                                    {!dashboardData?.captain_revenues?.length ? (
                                        <GraphQlResponseNoData className="!py-10 !mt-0" />
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <div className="min-w-[600px] lg:min-w-full">
                                                <table className="min-w-full">
                                                    <thead>
                                                        <tr className="border-b border-gray-200">
                                                            <th className="text-left py-2 text-gray-400 text-sm font-medium">
                                                                {t('Captain Name')}
                                                            </th>
                                                            <th className="text-center py-2 text-gray-400 text-sm font-medium">
                                                                {t('Revenue (BHD)')}
                                                            </th>
                                                            <th className="text-right py-2 text-gray-400 text-sm font-medium">
                                                                {t('Trips Completed')}
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100">
                                                        {dashboardData?.captain_revenues?.map((item, index) => (
                                                            <tr key={index} className="hover:bg-gray-50">
                                                                <td className="py-3 text-sm text-primary font-semibold">
                                                                    {item.captain_name}
                                                                </td>
                                                                <td className="py-3 text-sm text-primary font-semibold text-center">
                                                                    {item.total_revenue?.toFixed(2)}
                                                                </td>
                                                                <td className="py-3 text-sm text-primary font-semibold text-right">
                                                                    {item.trips_completed}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <h2 className="text-lg font-semibold mb-4">{t('Top Revenue-Generating Boats')}</h2>
                                    {!dashboardData?.boat_revenues?.length ? (
                                        <GraphQlResponseNoData className="!py-10 !mt-0" />
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <div className="min-w-[600px] lg:min-w-full">
                                                <table className="min-w-full">
                                                    <thead>
                                                        <tr className="border-b border-gray-200">
                                                            <th className="text-left py-2 text-gray-400 text-sm font-medium">
                                                                {t('Boat Name')}
                                                            </th>
                                                            <th className="text-center py-2 text-gray-400 text-sm font-medium">
                                                                {t('Revenue (BHD)')}
                                                            </th>
                                                            <th className="text-right py-2 text-gray-400 text-sm font-medium">
                                                                {t('Average Trip Value (BHD)')}
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100">
                                                        {dashboardData?.boat_revenues?.map((boat, index) => (
                                                            <tr key={index} className="hover:bg-gray-50">
                                                                <td className="py-3 text-sm text-primary font-semibold">
                                                                    {boat.boat_name.en}
                                                                </td>
                                                                <td className="py-3 text-sm text-primary font-semibold text-center">
                                                                    {boat.total_revenue?.toFixed(2)}
                                                                </td>
                                                                <td className="py-3 text-sm text-primary font-semibold text-right">
                                                                    {boat.avg_trip_value?.toFixed(2)}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

export default RevenueDashboard
